import React, { useCallback } from "react";
import { styled } from "@mui/material/styles";
import {
  TextareaAutosize,
  TextareaAutosizeProps,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";

const CustomTextFieldStyled = styled((props: TextFieldProps) => (
  <TextField {...props} />
))(({ theme }) => ({
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },

  /* ---------------- LABEL COLORS ---------------- */
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    marginTop: "-0.4rem",
    color: "#757575",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#5a5a5a",
  },
  "& .MuiInputLabel-root.Mui-error": {
    color: "#dc2626",
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "#757575",
  },
  "& .MuiInputLabel-shrink": {
    fontSize: "0.85rem",
    fontWeight: 600,
    marginTop: "0.1rem",
    color: "#5a5a5a",
  },

  /* ---------------- NUMBER INPUT FIX ---------------- */

  "& input[type=number]::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },

  /* ---------------- INPUT ---------------- */

  "& .MuiOutlinedInput-input": {
    padding: "9px 13px",
    fontSize: "0.87rem",
    color: "#454545",
    fontWeight: 600,
  },

  "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-multiline": {
    padding: "0px",
  },

  "& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputMultiline": {
    padding: "9px 13px",
    fontSize: "0.8rem",
    color: "#454545",
    fontWeight: 600,
  },

  /* ---------------- PLACEHOLDER ---------------- */

  "& .MuiOutlinedInput-input::placeholder": {
    color: "#767e89",
    opacity: 1,
    fontWeight: 600,
  },

  /* ---------------- BORDER ---------------- */

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor:
      theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#757575",
    borderRadius: "5px",
    // borderWidth: 2,
  },

  /* ---------------- DISABLED ---------------- */

  "& .MuiOutlinedInput-input.Mui-disabled": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#f8f9fb",
    fontWeight: 600,
    WebkitTextFillColor: "rgba(0, 0, 0, 0.55)",
    cursor: "not-allowed",
  },

  "& .MuiOutlinedInput-input.Mui-disabled::placeholder": {
    color: "#383838ff",
    opacity: 1,
  },
}));

export const CustomTextField: React.FC<
  TextFieldProps & { readOnlyMode?: boolean }
> = (props) => {
  const { readOnlyMode = false, ...rest } = props;

  if (readOnlyMode) {
    return (
      <Typography variant="body1" padding={1} fontSize={"0.8rem"}>
        <>{props.value || "--"}</>
      </Typography>
    );
  }

  return (
    <>
      <CustomTextFieldStyled {...rest} />
    </>
  );
};

export const ExpandableCustomTextFieldStyled = styled(
  (props: TextareaAutosizeProps) => <TextareaAutosize {...props} />,
)(({ theme }) => ({
  // Styles for native textarea (TextareaAutosize) instead of MUI TextField
  padding: "9px 13px",
  fontSize: "0.8rem",
  margin: 0,
  color: "rgb(38, 38, 38)",
  border: `1px solid ${
    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#dee3e9"
  }`,
  borderRadius: "5px",
  outline: "none",
  resize: "vertical",
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#fff",
  "&::placeholder": {
    color: "#767e89",
    opacity: 1,
  },
  "&:disabled": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#f8f9fb",
    color: "#767e89",
  },
}));

export const ExpandableTextField: React.FC<TextareaAutosizeProps> = (props) => {
  const { minRows, ...rest } = props;
  return (
    <ExpandableCustomTextFieldStyled
      {...rest}
      minRows={minRows}
      style={{ width: "100%" }}
    />
  );
};

interface CustomNumberFieldProps extends Omit<TextFieldProps, "value"> {
  readOnlyMode?: boolean;
  value?: string | number;
}

export const CustomNumberField: React.FC<CustomNumberFieldProps> = (props) => {
  const { readOnlyMode = false, value, onChange, ...rest } = props;

  // Process Initial Value
  const processedValue = useCallback(() => {
    if (value === undefined || value === null || value === "") return "";
    const valStr = value.toString();

    if (valStr.startsWith("0.") || valStr === "0") {
      return valStr; // Keep "0." and "0"
    }

    return valStr.replace(/^0+/, ""); // Remove leading zeros from integers
  }, [value])();

  // ReadOnly Mode: Display text instead of input
  if (readOnlyMode) {
    return (
      <Typography variant="body1" padding={1} fontSize="0.8rem">
        {value || "--"}
      </Typography>
    );
  }

  return (
    <CustomTextFieldStyled
      {...rest}
      value={processedValue} // Use processed value correctly
      onChange={(evt) => {
        if (!props.onChange) return;
        if (props.value?.toString().includes(".") && evt.target.value === ".")
          return;
        let newEvt = { ...evt };
        const onlyNums = newEvt.target.value
          .replace(/[^0-9.]/g, "")
          .replace(/(\..*)\./g, "$1");
        newEvt.target.value = onlyNums;
        props.onChange(newEvt);
      }}
      InputProps={{ inputProps: { min: 0 } }} // Prevents negative values in UI, allow zero
    />
  );
};
